import { wrapCreateBrowserRouter } from '@sentry/react';
import {
  RouterProvider as BaseRouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from 'react-router';
import AuthenticatedRouteApp from '@/router/appRoots/AuthenticatedAppRoot';
import BaseAppRoot from '@/router/appRoots/BaseAppRoot';
import InternalAdminAppRoot from '@/router/appRoots/InternalAdminAppRoot';
import { BASE_INTERNAL_URL } from '@/router/constants';
import { devRoutes } from '@/router/devRoutes';
import { StudentAppSessionRouteGuard } from '@/router/guards/StudentAppSessionRouteGuard';
import { internalAdminRoutes } from '@/router/internalAdminRoutes';
import { authenticatedRoutes, publicRoutes } from '@/router/routes';
import { toRouteElement } from '@/router/toRouteElement';
import NotFound from '@/routes/NotFound';
import schoolRouter from '@/routes/SchoolApp/router';
import { isProductionEnv } from '@/utils/appEnvironment';

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

/**
 * The single source of truth for the app's route composition.
 * Defined once here so the production browser router and any in-memory test
 * harness (router smoke characterization) mount the same real `<Route>` tree.
 */
const appRouteTree = createRoutesFromElements(
  <Route path={''} element={<BaseAppRoot />}>
    {toRouteElement(publicRoutes.Home)}
    {toRouteElement(publicRoutes.Login)}
    {toRouteElement(publicRoutes.Signup)}
    {toRouteElement(publicRoutes.ForgotPassword)}
    {toRouteElement(publicRoutes.PdfRenderer)}
    {toRouteElement(publicRoutes.Portfolio)}
    {toRouteElement(publicRoutes.PortfolioVoice)}
    {toRouteElement(publicRoutes.EnrollmentActivation)}
    {toRouteElement(publicRoutes.Enrollment)}
    {toRouteElement(publicRoutes.EnrollmentPayment)}
    {toRouteElement(publicRoutes.EnrollmentPaymentComplete)}

    <Route element={<AuthenticatedRouteApp />}>
      {toRouteElement(authenticatedRoutes.SplashScreen)}
      {toRouteElement(authenticatedRoutes.Avatar)}
      {toRouteElement(authenticatedRoutes.Classrooms)}

      <Route element={<StudentAppSessionRouteGuard />}>
        {toRouteElement(authenticatedRoutes.Lessons)}
        {toRouteElement(authenticatedRoutes.LiveSessionGame)}
        {toRouteElement(authenticatedRoutes.LiveSessionResult)}
        {toRouteElement(authenticatedRoutes.LessonDetail)}
        {toRouteElement(authenticatedRoutes.LessonComplete)}
        {toRouteElement(authenticatedRoutes.LessonActivities)}
        {toRouteElement(authenticatedRoutes.LessonReport)}
        {toRouteElement(authenticatedRoutes.LessonReportDetail)}
        {toRouteElement(authenticatedRoutes.LessonReportDetailV2)}
        {toRouteElement(authenticatedRoutes.LessonActivityReport)}
        {toRouteElement(authenticatedRoutes.Leaderboard)}
        {toRouteElement(authenticatedRoutes.LeaderboardPodium)}
        {toRouteElement(authenticatedRoutes.ActivitySessionV2)}
        {toRouteElement(authenticatedRoutes.ActivitySessionV2SplashScreen)}
        {toRouteElement(authenticatedRoutes.ActivitySessionV2ActivityIntro)}
        {toRouteElement(authenticatedRoutes.ActivitySessionV2Complete)}
        {toRouteElement(authenticatedRoutes.ActivitySessionV2PronunciationFeedback)}
        {toRouteElement(authenticatedRoutes.ActivitySessionV2ListeningReplay)}
        {toRouteElement(authenticatedRoutes.XP)}
        {toRouteElement(authenticatedRoutes.Coin)}
      </Route>

      {toRouteElement(authenticatedRoutes.UserNotebook)}
      {toRouteElement(authenticatedRoutes.Profile)}
      {devRoutes.map(toRouteElement)}
    </Route>

    {schoolRouter}

    {/* Internal Admin App */}
    <Route path={BASE_INTERNAL_URL}>
      <Route element={<InternalAdminAppRoot />}>
        {toRouteElement(internalAdminRoutes.Home)}
        {toRouteElement(internalAdminRoutes.VocabSearchRedirect)}
        {toRouteElement(internalAdminRoutes.VocabLibrary)}
        {toRouteElement(internalAdminRoutes.VocabEntryAdd)}
        {toRouteElement(internalAdminRoutes.VocabEntryEdit)}
        {toRouteElement(internalAdminRoutes.FeatureFlags)}
        {toRouteElement(internalAdminRoutes.Users)}
        {toRouteElement(internalAdminRoutes.UserDetail)}
        {toRouteElement(internalAdminRoutes.UserLessonDetail)}
        {toRouteElement(internalAdminRoutes.Schools)}
        {toRouteElement(internalAdminRoutes.SchoolDetail)}
        {toRouteElement(internalAdminRoutes.EditSchoolDetail)}
        {toRouteElement(internalAdminRoutes.SchoolDetailReport)}
        {toRouteElement(internalAdminRoutes.SchoolDetailIndividualReport)}
        {toRouteElement(internalAdminRoutes.ClassroomDetail)}
        {toRouteElement(internalAdminRoutes.ClassroomReorderLessons)}
        {toRouteElement(internalAdminRoutes.ClassroomLessonCreate)}
        {toRouteElement(internalAdminRoutes.ClassroomLessonImportCSV)}
        {toRouteElement(internalAdminRoutes.ClassroomModularLessonCreate)}
        {toRouteElement(internalAdminRoutes.LessonDetail)}
        {toRouteElement(internalAdminRoutes.DataExports)}
        {toRouteElement(internalAdminRoutes.BulkImport)}
        {toRouteElement(internalAdminRoutes.BulkImportV2)}
        {toRouteElement(internalAdminRoutes.ActivityBank)}
        {toRouteElement(internalAdminRoutes.ActivityBankSimpleQuiz)}
        {/* Asset upload is prod-only (LX-1062): the backend refuses uploads off production,
            so non-production builds don't register the route and deep links fall to the 404. */}
        {isProductionEnv() && toRouteElement(internalAdminRoutes.AssetUpload)}
        {toRouteElement(internalAdminRoutes.ActivityPreview)}
        {toRouteElement(internalAdminRoutes.ContentBuilder)}
        {toRouteElement(internalAdminRoutes.CreateEdetAccessCode)}
        {toRouteElement(internalAdminRoutes.Items)}
        {toRouteElement(internalAdminRoutes.AiLessonGenerator)}
        {toRouteElement(internalAdminRoutes.LessonBlueprints)}
        {toRouteElement(internalAdminRoutes.LessonBlueprintDetail)}
      </Route>
    </Route>

    {/* Reference for NotFound page: https://reactrouter.com/en/main/upgrading/v5#upgrade-all-switch-elements-to-routes */}
    <Route path="*" element={<NotFound />} />
  </Route>
);

const router = sentryCreateBrowserRouter(appRouteTree);

const RouterProvider = () => {
  return <BaseRouterProvider router={router} />;
};

export { RouterProvider, appRouteTree };
